import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_LOCALE, LOCALE_META, formatDate as fmtDate, formatDateTime as fmtDateTime,
  formatMoney as fmtMoney, formatNumber as fmtNumber, formatPercent as fmtPercent,
  interpolate, isLocale, negotiateLocale, type Locale,
} from '@/brand/i18n';
import { getDictionary, type Dictionary } from './dictionaries';

const STORAGE_KEY = 'nuat-portal-locale';

interface LocaleState {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  isRtl: boolean;
  t: Dictionary;
  setLocale: (next: Locale) => void;
  /** Formatters bound to the active locale, so call sites never pass it explicitly. */
  n: (value: number) => string;
  money: (value: number, currency?: string) => string;
  moneyExact: (value: number, currency?: string) => string;
  percent: (value: number) => string;
  date: (value: string | Date | null | undefined) => string;
  dateTime: (value: string | Date | null | undefined) => string;
  fill: (template: string, values: Record<string, string | number>) => string;
}

const LocaleContext = createContext<LocaleState | null>(null);

/** A stored choice wins; otherwise fall back to what the browser asks for. */
function initialLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    /* private browsing */
  }
  return negotiateLocale(navigator.languages?.join(',') ?? navigator.language) || DEFAULT_LOCALE;
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  // The `dir` attribute is what flips every logical Tailwind utility in the app, so it
  // has to live on <html>, not on a wrapper div.
  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = LOCALE_META[locale].dir;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* preference simply will not persist */
    }
  }, []);

  const value = useMemo<LocaleState>(() => {
    const t = getDictionary(locale);
    return {
      locale,
      dir: LOCALE_META[locale].dir,
      isRtl: LOCALE_META[locale].dir === 'rtl',
      t,
      setLocale,
      n: (v) => fmtNumber(v, locale),
      money: (v, currency) => fmtMoney(v, locale, currency, { maximumFractionDigits: 0 }),
      moneyExact: (v, currency) => fmtMoney(v, locale, currency),
      percent: (v) => fmtPercent(v, locale),
      date: (v) => fmtDate(v, locale),
      dateTime: (v) => fmtDateTime(v, locale),
      fill: interpolate,
    };
  }, [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleState {
  const context = useContext(LocaleContext);
  if (!context) throw new Error('useLocale must be used inside a LocaleProvider');
  return context;
}
